import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Cookie, Settings } from "lucide-react";
import { Link } from "react-router-dom";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const CONSENT_STORAGE_KEY = "hticu-cookie-consent";
const PREFERENCES_STORAGE_KEY = "hticu-cookie-preferences";

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    console.log("[CookieConsent] Component mounted");
    
    // Clean up old consent manager data
    localStorage.removeItem("consent-storage-key");
    
    const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
    const savedPrefs = localStorage.getItem(PREFERENCES_STORAGE_KEY);

    if (savedPrefs) {
      setPreferences(JSON.parse(savedPrefs));
    }

    // Show banner if no consent given
    if (!consent) {
      // Delay to avoid layout shift
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Apply saved preferences
      applyConsent(savedPrefs ? JSON.parse(savedPrefs) : preferences);
    }
  }, []);

  const applyConsent = (prefs: CookiePreferences) => {
    // Gate analytics scripts
    if (prefs.analytics) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }

    // Gate marketing scripts
    if (prefs.marketing) {
      enableMarketing();
    } else {
      disableMarketing();
    }

    // Functional cookies (preferences, theme, etc)
    if (prefs.functional) {
      enableFunctional();
    } else {
      disableFunctional();
    }
  };

  const enableAnalytics = () => {
    // Enable Google Analytics / plausible / etc
    console.log("[CookieConsent] Analytics enabled");
    
    // Example: Load GA script
    // const script = document.createElement('script');
    // script.src = 'https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID';
    // document.head.appendChild(script);
  };

  const disableAnalytics = () => {
    console.log("[CookieConsent] Analytics disabled");
    // Remove analytics cookies
    document.cookie.split(";").forEach((c) => {
      if (c.trim().startsWith("_ga") || c.trim().startsWith("_gid")) {
        document.cookie = c.trim().split("=")[0] + "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/";
      }
    });
  };

  const enableMarketing = () => {
    console.log("[CookieConsent] Marketing enabled");
    // Enable marketing pixels (Facebook, LinkedIn, etc)
  };

  const disableMarketing = () => {
    console.log("[CookieConsent] Marketing disabled");
    // Remove marketing cookies
  };

  const enableFunctional = () => {
    console.log("[CookieConsent] Functional enabled");
  };

  const disableFunctional = () => {
    console.log("[CookieConsent] Functional disabled");
  };

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(CONSENT_STORAGE_KEY, "true");
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    applyConsent(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    });
  };

  const acceptEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
    });
  };

  const saveCustom = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      {!showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 pointer-events-none">
          <Card className="max-w-4xl mx-auto pointer-events-auto shadow-strong border-2">
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                <Cookie className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 space-y-2">
                <h3 className="font-heading font-semibold text-lg">
                  We Value Your Privacy
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We use cookies to enhance your browsing experience, analyze site traffic, and 
                  personalize content. By clicking "Accept All", you consent to our use of cookies. 
                  You can customize your preferences or accept only essential cookies.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Link to="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>
                  <span className="text-muted-foreground">•</span>
                  <Link to="/terms" className="text-primary hover:underline">
                    Terms of Service
                  </Link>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={acceptAll} className="flex-1 sm:flex-none">
                Accept All
              </Button>
              <Button onClick={acceptEssential} variant="outline" className="flex-1 sm:flex-none">
                Essential Only
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                className="flex-1 sm:flex-none"
              >
                <Settings className="h-4 w-4 mr-2" />
                Customize
              </Button>
            </div>
          </div>
        </Card>
      </div>
      )}

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
            <DialogDescription>
              Manage your cookie settings. Essential cookies are always enabled as they're 
              necessary for the website to function properly.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Essential Cookies */}
            <div className="space-y-3 p-4 border border-border rounded-lg bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1">
                  <Label className="font-semibold">Essential Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Required for basic site functionality, security, and user authentication. 
                    These cannot be disabled.
                  </p>
                </div>
                <Switch checked disabled />
              </div>
              <p className="text-xs text-muted-foreground">
                Examples: Session cookies, security tokens, load balancing
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="space-y-3 p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1">
                  <Label htmlFor="analytics" className="font-semibold">
                    Analytics Cookies
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Help us understand how you use our website so we can improve your experience.
                  </p>
                </div>
                <Switch
                  id="analytics"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, analytics: checked })
                  }
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Examples: Google Analytics, page views, Core Web Vitals
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className="space-y-3 p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1">
                  <Label htmlFor="marketing" className="font-semibold">
                    Marketing Cookies
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Track your activity to deliver relevant advertisements and measure campaign effectiveness.
                  </p>
                </div>
                <Switch
                  id="marketing"
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, marketing: checked })
                  }
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Examples: Facebook Pixel, LinkedIn Insight Tag, ad retargeting
              </p>
            </div>

            {/* Functional Cookies */}
            <div className="space-y-3 p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1">
                  <Label htmlFor="functional" className="font-semibold">
                    Functional Cookies
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Remember your preferences and personalized settings for a better experience.
                  </p>
                </div>
                <Switch
                  id="functional"
                  checked={preferences.functional}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, functional: checked })
                  }
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Examples: Language preference, theme selection, video player settings
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button onClick={saveCustom} className="w-full sm:w-auto">
              Save Preferences
            </Button>
            <Button onClick={acceptAll} variant="outline" className="w-full sm:w-auto">
              Accept All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
